import { InjectionToken } from '@angular/core';
import { environment } from '@ups/xplat/core';
import {
  describeRynReplayReadFailure,
  describeRynReplayReadNoToken,
} from './ryn-replay-read-failure';
import { RynReplayAccessToken } from './ryn-replay-access-token';
import {
  IRynReplayBackendTelemetry,
  RynReplayBackendStatus,
} from './ryn-replay-backend-telemetry';
import { RynReplayLoadError } from './ryn-replay-event-source';

/**
 * Lazy App Insights lookup for the Backend tab. Queried on tab open, never at
 * ingest, so replay storage and AI retention stay independent.
 */
export interface IRynReplayBackendTelemetrySource {
  load(
    sessionId: string,
    operationIds: string[]
  ): Promise<IRynReplayBackendTelemetry>;
}

export const RYN_REPLAY_BACKEND_TELEMETRY_SOURCE =
  new InjectionToken<IRynReplayBackendTelemetrySource>(
    'RYN_REPLAY_BACKEND_TELEMETRY_SOURCE'
  );

const STATUSES: readonly RynReplayBackendStatus[] = [
  'ok',
  'empty',
  'notConfigured',
  'unavailable',
];

export class RynReplayHttpBackendTelemetrySource implements IRynReplayBackendTelemetrySource {
  constructor(
    private readonly accessToken: () => Promise<RynReplayAccessToken>,
    private readonly fetchRef: typeof fetch = globalThis.fetch?.bind(globalThis)
  ) {}

  async load(
    sessionId: string,
    operationIds: string[]
  ): Promise<IRynReplayBackendTelemetry> {
    const base = environment.rynReplay?.readApiUrl?.replace(/\/+$/, '');
    if (!base) {
      throw new RynReplayLoadError(
        'The replay read API is not configured for this app, so server telemetry cannot be loaded.'
      );
    }

    const access = await this.accessToken();
    if (!access.token) {
      throw new RynReplayLoadError(describeRynReplayReadNoToken(access.reason));
    }
    const token = access.token;

    const response = await this.fetchRef(
      `${base}/sessions/${encodeURIComponent(sessionId)}/backend-telemetry`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ operationIds }),
      }
    );

    if (!response.ok) {
      throw new RynReplayLoadError(
        describeRynReplayReadFailure(response.status)
      );
    }

    const json = (await response.json()) as {
      status?: unknown;
      operations?: unknown;
    };
    const status = STATUSES.includes(json.status as RynReplayBackendStatus)
      ? (json.status as RynReplayBackendStatus)
      : 'empty';
    const operations = Array.isArray(json.operations) ? json.operations : [];
    return {
      status,
      operations: operations as IRynReplayBackendTelemetry['operations'],
    };
  }
}
